import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Clock, Briefcase, MapPin, Bot } from 'lucide-react';
import { CandidateForRecommendation, DavidConversation, getDavidConversations, getDavidMessages } from '../services/recommendationsApi';

interface DavidConversationsPanelProps {
    candidates: CandidateForRecommendation[];
}

interface DavidMessage {
    id: string;
    conversation_id: string;
    sender_type: 'david_bot' | 'candidate';
    sender_name: string;
    content: string;
    message_type: 'text' | 'job_recommendation';
    job_recommendations?: {
        job_id: string;
        title: string;
        company_name: string;
        location: string;
        employment_type: string;
        match_score: number;
    }[];
    created_at: string;
}

const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatFullDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
};

const DavidConversationsPanel: React.FC<DavidConversationsPanelProps> = ({ candidates }) => {
    const [conversations, setConversations] = useState<DavidConversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<DavidConversation | null>(null);
    const [messages, setMessages] = useState<DavidMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Build candidateId -> name lookup from candidates prop
    const candidateNameMap = React.useMemo(() => {
        const map = new Map<string, string>();
        candidates.forEach(c => {
            const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
            if (name) map.set(c.id, name);
        });
        return map;
    }, [candidates]);

    const resolveName = (conv: DavidConversation) =>
        conv.candidateName || candidateNameMap.get(conv.candidateId) || conv.candidateEmail || 'Unknown';

    useEffect(() => {
        loadConversations();
    }, []);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const data = await getDavidConversations();
            setConversations(data);
            if (data.length > 0 && !selectedConversation) {
                selectConversation(data[0]);
            }
        } catch (err) {
            console.error('Failed to load conversations:', err);
        } finally {
            setLoading(false);
        }
    };

    const selectConversation = async (conv: DavidConversation) => {
        setSelectedConversation(conv);
        setMessagesLoading(true);
        try {
            const msgs = await getDavidMessages(conv.id);
            setMessages(msgs);
        } catch (err) {
            console.error('Failed to load messages:', err);
            setMessages([]);
        } finally {
            setMessagesLoading(false);
        }
    };

    const getInitials = (name?: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
    };

    const getMatchColor = (score: number) => {
        if (score >= 0.7) return 'text-emerald-600 bg-emerald-50';
        if (score >= 0.4) return 'text-amber-600 bg-amber-50';
        return 'text-[#757575] bg-[#F1F3F4]';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-3 border-[#015FA6]/20 border-t-[#015FA6] rounded-full animate-spin" />
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <MessageCircle className="w-20 h-20 mb-4 text-[#CBCBCB]" strokeWidth={1} />
                <p className="text-[#A5A5A5] text-sm font-medium">No candidates have been contacted yet</p>
                <p className="text-[#CBCBCB] text-xs mt-1">Send job recommendations to candidates to start conversations</p>
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-320px)] min-h-[500px] -mx-6 -mb-6 border-t border-[#EDEDED]">
            {/* Left: Conversation List */}
            <div className="w-80 border-r border-[#EDEDED] flex flex-col bg-[#FAFBFC]">
                <div className="px-4 py-3 border-b border-[#EDEDED]">
                    <p className="text-xs font-semibold text-[#757575] uppercase tracking-wider">
                        Conversations ({conversations.length})
                    </p>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {conversations.map(conv => (
                        <button
                            key={conv.id}
                            onClick={() => selectConversation(conv)}
                            className={`w-full text-left px-4 py-3.5 border-b border-[#F1F3F4] transition-colors ${
                                selectedConversation?.id === conv.id
                                    ? 'bg-[#015FA6]/5 border-l-2 border-l-[#015FA6]'
                                    : 'hover:bg-white border-l-2 border-l-transparent'
                            }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-[#015FA6] to-[#0077cc] shrink-0">
                                    {getInitials(resolveName(conv))}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <p className={`text-sm truncate ${
                                            selectedConversation?.id === conv.id ? 'font-semibold text-[#0B111E]' : 'font-medium text-[#515151]'
                                        }`}>
                                            {resolveName(conv)}
                                        </p>
                                        <span className="text-[10px] text-[#A5A5A5] shrink-0">
                                            {formatTime(conv.updatedAt || conv.createdAt)}
                                        </span>
                                    </div>
                                    {conv.lastMessage && (
                                        <p className="text-xs text-[#A5A5A5] truncate mt-0.5">
                                            {conv.lastMessage.content}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Right: Message Timeline */}
            <div className="flex-1 flex flex-col bg-white">
                {selectedConversation ? (
                    <>
                        {/* Chat Header */}
                        <div className="px-6 py-4 border-b border-[#EDEDED] flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold bg-gradient-to-br from-[#015FA6] to-[#0077cc]">
                                {getInitials(resolveName(selectedConversation))}
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-[#0B111E]">
                                    {resolveName(selectedConversation)}
                                </h3>
                                {selectedConversation.candidateEmail && (
                                    <p className="text-xs text-[#757575]">{selectedConversation.candidateEmail}</p>
                                )}
                            </div>
                            <div className="ml-auto flex items-center gap-1.5 text-xs text-[#A5A5A5]">
                                <Clock size={12} />
                                Started {formatFullDate(selectedConversation.createdAt)}
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                            {messagesLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <div className="w-8 h-8 border-2 border-[#015FA6]/20 border-t-[#015FA6] rounded-full animate-spin" />
                                </div>
                            ) : messages.length === 0 ? (
                                <div className="text-center py-10 text-sm text-[#A5A5A5]">
                                    No messages in this conversation
                                </div>
                            ) : (
                                messages.map(msg => (
                                    <div key={msg.id} className="flex gap-3">
                                        {/* Avatar */}
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                                            msg.sender_type === 'david_bot'
                                                ? 'bg-gradient-to-br from-[#015FA6] to-[#0077cc]'
                                                : 'bg-gradient-to-br from-emerald-500 to-emerald-600'
                                        }`}>
                                            {msg.sender_type === 'david_bot' ? (
                                                <Bot size={14} className="text-white" />
                                            ) : (
                                                <span className="text-white text-xs font-bold">
                                                    {getInitials(resolveName(selectedConversation))}
                                                </span>
                                            )}
                                        </div>

                                        {/* Message Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-semibold text-[#0B111E]">
                                                    {msg.sender_type === 'david_bot' ? 'David' : resolveName(selectedConversation)}
                                                </span>
                                                <span className="text-[10px] text-[#A5A5A5]">
                                                    {formatFullDate(msg.created_at)}
                                                </span>
                                            </div>

                                            {/* Text Content */}
                                            <div className="bg-[#F8F9FA] rounded-xl rounded-tl-sm px-4 py-3 inline-block max-w-[90%]">
                                                <p className="text-sm text-[#333] whitespace-pre-line">{msg.content}</p>
                                            </div>

                                            {/* Job Recommendation Cards */}
                                            {msg.job_recommendations && msg.job_recommendations.length > 0 && (
                                                <div className="mt-2 space-y-2 max-w-[90%]">
                                                    {msg.job_recommendations.map((job, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="border border-[#E3E8EF] rounded-xl p-4 bg-white hover:shadow-sm transition-shadow"
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0 flex-1">
                                                                    <h4 className="text-sm font-semibold text-[#0B111E] leading-tight">
                                                                        {job.title}
                                                                    </h4>
                                                                    <p className="text-xs text-[#515151] mt-0.5">
                                                                        {job.company_name}
                                                                    </p>
                                                                </div>
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${getMatchColor(job.match_score)}`}>
                                                                    {Math.round(job.match_score * 100)}% match
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-3 mt-2.5">
                                                                <span className="inline-flex items-center gap-1 text-xs text-[#757575]">
                                                                    <MapPin size={11} />
                                                                    {job.location}
                                                                </span>
                                                                <span className="inline-flex items-center gap-1 text-xs text-[#757575]">
                                                                    <Briefcase size={11} />
                                                                    {job.employment_type}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 border-t border-[#EDEDED] bg-[#FAFBFC]">
                            <p className="text-xs text-[#A5A5A5] text-center">
                                Messages are sent as David. Candidates cannot reply to these messages.
                            </p>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-sm text-[#A5A5A5]">
                        Select a conversation to view messages
                    </div>
                )}
            </div>
        </div>
    );
};

export default DavidConversationsPanel;
